// What the GIF picker searches for before anybody types. Lifted as is from the
// Andon app's `lib/gifSeed.ts`.
//
// WHY SEED IT AT ALL
//
// "Type something to search" is a blank page, and a blank page on a phone
// beside a stopped machine is a thing people close. The picker is for a
// reaction — somebody says thanks, or acknowledges a wait, or marks a fix — and
// the reaction is nearly always to the SITUATION rather than to the subject.
//
// WHY NOT SEARCH THE SUBJECT
//
// The obvious idea is to search what the andon is about: "missing part",
// "bracket", "Steel Cutting". It produces nothing usable. Giphy has no GIFs of
// a missing 60mm bracket, and the ones it returns for those words are noise
// that has to be cleared before the picker is of any use — worse than the blank
// page, because it looks like the feature tried and failed.
//
// WHY EACH CUE HAS SEVERAL WORDS
//
// One term per cue meant one search, and one search on Giphy means ONE SET OF
// GIFS — its ranking is stable, so "thank you" returns the same two dozen
// today as yesterday. The picker felt like a fixed menu rather than a search.
// Each cue is now a handful of ways to say the same thing, chosen from at
// random, which changes the query itself; the server varies the page within
// that query. Two different sources of variety, because either alone still
// repeats quickly.
//
// THE TERMS STAY IN ENGLISH. They are what the BFF sends to Giphy, not what
// anybody reads — the box the picker draws is empty, and the only words on
// screen are the picker's own, which are TR keys.

import type { AndonAlertVM, ThreadMessageVM } from '@/api/andon/types';

/** Pick one of several. Injected so tests are not at the mercy of a coin. */
export type Pick = <T>(options: readonly T[]) => T;

const randomPick: Pick = (options) => options[Math.floor(Math.random() * options.length)]!;

/**
 * Cue words, and the ways of answering each one.
 *
 * Ordered, and the order is the priority: a message saying "sorry, my fault"
 * is an apology first and a fix second. Matched as whole words so
 * "thanksgiving" is not gratitude and "sorted" is not "sorry".
 */
const CUES: ReadonlyArray<readonly [RegExp, readonly string[]]> = [
  [/\b(thanks|thank you|cheers|legend|ta)\b/i, ['thank you', 'thanks', 'grateful', 'appreciate it', 'high five', 'you are the best']],
  [/\b(sorry|my fault|apologies|oops)\b/i, ['apology', 'sorry', 'my bad', 'awkward', 'facepalm', 'oops']],
  [/\b(nice|great|awesome|beauty|good work|well done)\b/i, ['celebrate', 'nailed it', 'applause', 'thumbs up', 'victory dance', 'well done']],
  [/\b(hurry|asap|urgent|quick|now please)\b/i, ['hurry up', 'running late', 'fast', 'come on', 'tick tock']],
  [/\b(waiting|still waiting|any update|hello\?)\b/i, ['waiting', 'still waiting', 'bored', 'checking watch', 'any day now']],
  [/\b(on my way|omw|coming|two minutes|be right there|bringing)\b/i, ['on my way', 'running', 'almost there', 'be right there', 'incoming']],
  [/\b(fixed|sorted|done|running|back up|all good)\b/i, ['celebrate', 'mission accomplished', 'success', 'like a boss', 'fixed it']],
  [/\b(stuck|jammed|broken|not working|no idea)\b/i, ['confused', 'shrug', 'what is happening', 'broken', 'this is fine']],
];

/** What the situation itself calls for, when nothing has been said. */
const SITUATIONS = {
  // Closed: the fix has landed and the line is running. Gratitude and
  // finishing, not urgency.
  settled: ['thank you', 'thanks', 'nailed it', 'teamwork', 'good job'],
  working: ['working on it', 'busy', 'focused', 'on it', 'hard at work'],
  // Nobody has come and the promise has run out. Not the place for jokes, and
  // also exactly when somebody sends one — so meet them there rather than
  // pretend.
  waiting: ['waiting', 'still waiting', 'patience', 'any day now', 'tapping fingers'],
  coming: ['on my way', 'almost there', 'incoming', 'running', 'be right there'],
} as const;

function situationOf(alert: AndonAlertVM): readonly string[] {
  if (!alert.open) return SITUATIONS.settled;
  if (alert.responding) return SITUATIONS.working;
  if (alert.sla.breaching) return SITUATIONS.waiting;
  if (alert.acked) return SITUATIONS.coming;
  return SITUATIONS.waiting;
}

/**
 * The term to search when the picker opens.
 *
 * Recent messages first, newest backwards, because a conversation's last few
 * lines are what a reaction is to. Capped at the last handful: a cue six
 * messages ago is not what anybody is answering, and reaching further back
 * makes the seed feel arbitrary rather than attentive.
 */
export function gifSeed(
  alert: AndonAlertVM,
  messages: readonly ThreadMessageVM[] = [],
  pick: Pick = randomPick,
  depth = 4,
): string {
  const recent = messages.slice(-depth).reverse();
  for (const message of recent) {
    const body = message.body ?? '';
    if (!body.trim()) continue;
    for (const [pattern, terms] of CUES) {
      if (pattern.test(body)) return pick(terms);
    }
  }
  return pick(situationOf(alert));
}

/**
 * Every SET the picker chooses from, and every term across all of them.
 *
 * Two shapes because two different things are worth guarding. A term repeated
 * WITHIN a set is twice as likely to come up, which is how a random picker
 * quietly acquires a favourite. A term shared ACROSS sets is fine and often
 * right — "celebrate" suits both "nice work" and "it's fixed", and only one
 * set is ever in play for a given pick.
 */
export const SEED_SETS: ReadonlyArray<readonly string[]> = [...CUES.map(([, terms]) => terms), ...Object.values(SITUATIONS)];

export const SEED_TERMS: readonly string[] = SEED_SETS.flat();
